// Formatting helpers shared across pages. Times come from the backend as
// epoch seconds; render them in the user's locale.

export function formatTs(secs: number | null | undefined): string {
  if (secs == null) return "—";
  const d = new Date(secs * 1000);
  return d.toLocaleString("zh-CN", { hour12: false });
}

export function formatRelative(secs: number | null | undefined, now = Date.now()): string {
  if (secs == null) return "—";
  const diff = Math.round((now - secs * 1000) / 1000);
  if (diff < 60) return `${diff} 秒前`;
  if (diff < 3600) return `${Math.round(diff / 60)} 分钟前`;
  if (diff < 86400) return `${Math.round(diff / 3600)} 小时前`;
  if (diff < 86400 * 30) return `${Math.round(diff / 86400)} 天前`;
  return formatTs(secs);
}

function localCalendarDay(date: Date): number {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86_400_000;
}

function localDayDiff(secs: number, now: number): number {
  return localCalendarDay(new Date(now)) - localCalendarDay(new Date(secs * 1000));
}

function formatClock(date: Date): string {
  return date.toLocaleTimeString("zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

export type ConversationRecency = "today" | "threeDays" | "week" | "older";

export function conversationRecency(secs: number, now = Date.now()): ConversationRecency {
  const days = localDayDiff(secs, now);
  if (days <= 0) return "today";
  if (days <= 2) return "threeDays";
  if (days <= 6) return "week";
  return "older";
}

export function formatConversationTime(secs: number, now = Date.now()): string {
  const date = new Date(secs * 1000);
  if (localDayDiff(secs, now) <= 0) return formatClock(date);
  if (date.getFullYear() === new Date(now).getFullYear()) {
    return `${date.getMonth() + 1}月${date.getDate()}日`;
  }
  return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
}

export function formatMessageTime(secs: number, now = Date.now()): string {
  const date = new Date(secs * 1000);
  const days = localDayDiff(secs, now);
  const clock = formatClock(date);
  if (days <= 0) return `今天 ${clock}`;
  if (days === 1) return `昨天 ${clock}`;
  if (date.getFullYear() === new Date(now).getFullYear()) {
    return `${date.getMonth() + 1}月${date.getDate()}日 ${clock}`;
  }
  return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日 ${clock}`;
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function taskStateLabel(state: string): { label: string; cls: string } {
  switch (state) {
    case "queued":
      return { label: "排队中", cls: "bg-zinc-100 text-zinc-700" };
    case "running":
      return { label: "运行中", cls: "bg-blue-100 text-blue-700" };
    case "done":
      return { label: "已完成", cls: "bg-emerald-100 text-emerald-700" };
    case "error":
      return { label: "失败", cls: "bg-red-100 text-red-700" };
    case "cancelled":
      return { label: "已取消", cls: "bg-zinc-100 text-zinc-500" };
    default:
      return { label: state, cls: "bg-zinc-100 text-zinc-700" };
  }
}
